import React, { useMemo, useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { AgGridReact } from 'ag-grid-react';
import { Copy, Check, ChevronRight } from 'lucide-react';
import Drawer from '@/components/ui/Drawer';
import { themeQuartz } from 'ag-grid-community';
import { initializeAgGrid } from './rendering/ag-grid-setup';
import type { DashboardBlock, DataTransform } from '@/types/dashboard';
import type { ColDef } from 'ag-grid-community';

initializeAgGrid();

interface InspectBlockDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  block: DashboardBlock;
  data: Record<string, unknown>[];
}

type TabId = 'query' | 'data';

// ─── Helpers ────────────────────────────────────────────────────────────────

// The backend query_spec shape differs from the frontend type.
// Treat it loosely and safely extract display values.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyQS = Record<string, any>;

function formatTimeRange(tr: AnyQS | undefined): string {
  if (!tr) return 'Default';
  switch (tr.type) {
    case 'last_n_days':
      return `Last ${tr.value} days`;
    case 'last_n_weeks':
      return `Last ${tr.value} weeks`;
    case 'dashboard_controlled':
      return 'Dashboard controlled';
    case 'specific_range':
      return `${tr.start_date ?? '?'} to ${tr.end_date ?? '?'}`;
    default:
      return String(tr.type ?? 'Default').replace(/_/g, ' ');
  }
}

function formatOperator(op: string): string {
  const map: Record<string, string> = {
    eq: '=', neq: '!=', gt: '>', gte: '>=', lt: '<', lte: '<=',
    in: 'in', not_in: 'not in', like: 'like', is_null: 'is null',
    is_not_null: 'is not null',
  };
  return map[op] ?? op;
}

/**
 * AG Grid theme for the inspect drawer — adapts to light/dark via CSS vars
 * rather than the hard-coded dark dashboard tokens.
 */
const inspectGridTheme = themeQuartz.withParams({
  fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  fontSize: 13,
  spacing: 6,
  borderRadius: 0,
  wrapperBorderRadius: 8,
  headerFontWeight: 600,
  headerFontSize: 12,
});

/** Safely convert any value to a displayable string */
function displayValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object') {
    // Handle {field: "..."} objects common in query_spec
    if ('field' in (v as Record<string, unknown>)) return String((v as Record<string, unknown>).field);
    return JSON.stringify(v);
  }
  return String(v);
}

/** Extract metric display strings from the backend query_spec */
function extractMetrics(qs: AnyQS): string[] {
  // New shape: metrics: [{field, aggregations: string[]}]
  if (Array.isArray(qs.metrics)) {
    return qs.metrics.flatMap((m: AnyQS) => {
      const field = m.field ?? '?';
      const aggs: string[] = Array.isArray(m.aggregations) ? m.aggregations : [];
      if (aggs.length === 0) return [field];
      return aggs.map((a: string) => `${a.toUpperCase()} of ${field}`);
    });
  }
  // Legacy shape: metric + aggregation
  if (qs.metric) {
    const prefix = qs.aggregation ? `${String(qs.aggregation).toUpperCase()} of ` : '';
    return [`${prefix}${qs.metric}`];
  }
  return [];
}

/** Extract group_by field names — handles both string[] and {field}[] */
function extractGroupBy(qs: AnyQS): string[] {
  if (!Array.isArray(qs.group_by)) return [];
  return qs.group_by.map((g: unknown) => displayValue(g));
}

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Query tab: field row ───────────────────────────────────────────────────

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start gap-3 py-2">
    <span className="w-32 flex-shrink-0 text-xs font-medium text-muted-foreground pt-0.5">
      {label}
    </span>
    <div className="flex-1 text-sm text-foreground">
      {children}
    </div>
  </div>
);

const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-accent text-foreground">
    {children}
  </span>
);

// ─── Component ──────────────────────────────────────────────────────────────

const InspectBlockDrawer: React.FC<InspectBlockDrawerProps> = ({
  isOpen,
  onClose,
  block,
  data,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>('query');
  const [showRawJson, setShowRawJson] = useState(false);
  const [copied, setCopied] = useState(false);

  const qs = block.query_spec as AnyQS;
  const dt = block.data_transform;

  // AG Grid column defs — auto-generated from data keys
  const columnDefs: ColDef[] = useMemo(() => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).map((key) => ({
      field: key,
      headerName: titleCase(key),
      sortable: true,
      resizable: true,
      filter: true,
      flex: 1,
      minWidth: 120,
    }));
  }, [data]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(qs, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [qs]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Inspect: ${block.title}`}
      width="lg"
    >
      {/* Tabs */}
      <div className="flex-shrink-0 px-5 pt-4 pb-0">
        <div className="flex gap-1 p-0.5 rounded-lg bg-accent/50 w-fit">
          {(['query', 'data'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={clsx(
                'px-3.5 py-1.5 text-xs font-medium rounded-md transition-colors',
                activeTab === tab
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab === 'query' ? 'Query' : `Data (${data?.length ?? 0} rows)`}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'query' && (
          <div className="px-5 py-4 space-y-1">
            {/* Human-readable fields */}
            <div className="divide-y divide-border">
              {extractMetrics(qs).length > 0 && (
                <Field label="Metrics">
                  <div className="flex flex-wrap gap-1.5">
                    {extractMetrics(qs).map((m, i) => (
                      <Pill key={i}>{m}</Pill>
                    ))}
                  </div>
                </Field>
              )}

              <Field label="Data Source">
                <span className="font-mono text-xs text-muted-foreground">
                  {qs.data_source_account_id}
                </span>
              </Field>

              {extractGroupBy(qs).length > 0 && (
                <Field label="Grouped By">
                  <div className="flex flex-wrap gap-1.5">
                    {extractGroupBy(qs).map((g, i) => (
                      <Pill key={i}>{g}</Pill>
                    ))}
                  </div>
                </Field>
              )}

              <Field label="Time Range">
                {formatTimeRange(qs.time_range)}
              </Field>

              {Array.isArray(qs.conditions) && qs.conditions.length > 0 && (
                <Field label="Conditions">
                  <div className="space-y-1">
                    {qs.conditions.map((c: AnyQS, i: number) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <Pill>{displayValue(c.field)}</Pill>
                        <span className="text-xs text-muted-foreground">
                          {formatOperator(String(c.operator))}
                        </span>
                        <Pill>{displayValue(c.value)}</Pill>
                      </div>
                    ))}
                  </div>
                </Field>
              )}

              {qs.compare_to && (
                <Field label="Comparison">
                  <Pill>{titleCase(String(qs.compare_to.type ?? qs.compare_to))}</Pill>
                </Field>
              )}

              {qs.time_grouping && (
                <Field label="Time Grouping">
                  <Pill>{titleCase(String(qs.time_grouping))}</Pill>
                </Field>
              )}

              {qs.order_by && (
                <Field label="Order By">
                  <Pill>
                    {displayValue(qs.order_by.field ?? qs.order_by)} {qs.order_by.direction ?? ''}
                  </Pill>
                </Field>
              )}

              {qs.limit && (
                <Field label="Limit">
                  <Pill>{String(qs.limit)}</Pill>
                </Field>
              )}
            </div>

            {/* Data Transform section */}
            {dt && <DataTransformSection transform={dt} />}

            {/* Collapsible raw JSON */}
            <div className="pt-3">
              <button
                onClick={() => setShowRawJson((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronRight
                  className={clsx(
                    'w-3.5 h-3.5 transition-transform duration-200',
                    showRawJson && 'rotate-90'
                  )}
                />
                Raw Query JSON
              </button>

              {showRawJson && (
                <div className="relative mt-2 rounded-lg code-block border border-border">
                  <button
                    onClick={handleCopy}
                    title="Copy to clipboard"
                    className="absolute top-2 right-2 p-1.5 rounded-md text-muted-foreground hover:bg-background/10 transition-colors"
                  >
                    {copied ? (
                      <Check className="w-3.5 h-3.5 text-success" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <pre className="p-4 text-xs font-mono text-muted-foreground/60 overflow-auto max-h-80 whitespace-pre-wrap">
                    {JSON.stringify(qs, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'data' && (
          <div className="px-5 py-4">
            {data && data.length > 0 ? (
              <div style={{ height: 'calc(100vh - 160px)', width: '100%' }}>
                <AgGridReact
                  theme={inspectGridTheme}
                  columnDefs={columnDefs}
                  rowData={data}
                  domLayout="normal"
                  animateRows={false}
                  suppressFieldDotNotation={true}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                    filter: true,
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center" style={{ height: 300 }}>
                <p className="text-sm text-muted-foreground">
                  No data available for this block.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

// ─── Data Transform sub-section ─────────────────────────────────────────────

const DataTransformSection: React.FC<{ transform: DataTransform }> = ({ transform }) => {
  const entries: { label: string; value: string }[] = [];

  if (transform.excludeCategories?.length) {
    entries.push({ label: 'Exclude', value: transform.excludeCategories.join(', ') });
  }
  if (transform.includeCategories?.length) {
    entries.push({ label: 'Include Only', value: transform.includeCategories.join(', ') });
  }
  if (transform.absoluteValues?.length) {
    entries.push({ label: 'Absolute Values', value: transform.absoluteValues.join(', ') });
  }
  if (transform.renameFields && Object.keys(transform.renameFields).length > 0) {
    entries.push({
      label: 'Rename Fields',
      value: Object.entries(transform.renameFields).map(([k, v]) => `${k} → ${v}`).join(', '),
    });
  }
  if (transform.sortBy) {
    entries.push({ label: 'Sort By', value: transform.sortBy });
  }
  if (transform.limit) {
    entries.push({ label: 'Limit', value: String(transform.limit) });
  }

  if (entries.length === 0) return null;

  return (
    <div className="pt-3 border-t border-border/50">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
        Data Transform
      </h4>
      <div className="divide-y divide-border">
        {entries.map((e) => (
          <Field key={e.label} label={e.label}>
            <Pill>{e.value}</Pill>
          </Field>
        ))}
      </div>
    </div>
  );
};

export default InspectBlockDrawer;
